/**
 * Embedding rules for the chat widget when loaded inside an iframe.
 *
 * - Not in an iframe: always allowed (standalone / direct visit).
 * - In an iframe: parent origin must match REACT_APP_EMBED_ORIGINS (comma-separated origins).
 *   Rules may be exact origins or one wildcard per entry: https://*.rejara.com matches
 *   https://app.rejara.com, https://www.rejara.com, and https://rejara.com (apex).
 *   Default localhost / 127.0.0.1 / ::1 patterns apply when the list is empty (dev).
 *
 * document.referrer is the embedding page URL when available (may be empty in some browsers).
 * For production, also set CSP frame-ancestors on the server (see README).
 */

function stripOuterQuotes(s) {
  let t = String(s).trim();
  if (
    (t.startsWith("'") && t.endsWith("'")) ||
    (t.startsWith('"') && t.endsWith('"'))
  ) {
    t = t.slice(1, -1).trim();
  }
  return t;
}

function parseEmbedOrigins() {
  const raw = stripOuterQuotes(process.env.REACT_APP_EMBED_ORIGINS || '');
  return raw
    .split(',')
    .map((s) => stripOuterQuotes(s))
    .filter(Boolean);
}

/**
 * Wildcard rules: https://*.example.com or http://*.example.com
 * Matches apex (example.com) and any subdomain (app.example.com, a.b.example.com).
 */
function originMatchesWildcardRule(parentOrigin, rule) {
  const r = rule.replace(/\/$/, '');
  const m = r.match(/^(https?):\/\/\*\.(.+)$/i);
  if (!m) return false;
  const wantScheme = m[1].toLowerCase() + ':';
  const baseDomain = m[2].toLowerCase();
  try {
    const u = new URL(parentOrigin);
    if (u.protocol !== wantScheme) return false;
    const host = u.hostname.toLowerCase();
    if (host === baseDomain) return true;
    if (host.endsWith('.' + baseDomain)) return true;
    return false;
  } catch {
    return false;
  }
}

function originMatchesRule(parentOrigin, rule) {
  if (!rule) return false;
  const r = rule.replace(/\/$/, '');
  if (parentOrigin === r) return true;
  if (r.includes('*')) {
    return originMatchesWildcardRule(parentOrigin, rule);
  }
  // Convenience: "http://localhost" matches any port on localhost
  if (r === 'http://localhost' || r === 'http://127.0.0.1') {
    try {
      const u = new URL(parentOrigin);
      const hostOk =
        r === 'http://localhost' ? u.hostname === 'localhost' : u.hostname === '127.0.0.1';
      return hostOk && u.protocol === 'http:';
    } catch {
      return false;
    }
  }
  return false;
}

function isLocalDevOrigin(origin) {
  try {
    const u = new URL(origin);
    const h = u.hostname;
    return (
      h === 'localhost' ||
      h === '127.0.0.1' ||
      h === '[::1]' ||
      h === '::1'
    );
  } catch {
    return false;
  }
}

/**
 * @returns {{ allowed: boolean, reason?: string }}
 */
export function checkEmbedAllowed() {
  if (typeof window === 'undefined') {
    return { allowed: true };
  }

  try {
    if (window.self === window.top) {
      return { allowed: true };
    }
  } catch {
    // cross-origin access to top may throw; treat as embedded
  }

  const ref = document.referrer;
  if (!ref || ref.trim() === '') {
    const rawAllow = (process.env.REACT_APP_EMBED_ALLOW_EMPTY_REFERRER || '').trim().toLowerCase();
    const allowEmpty = rawAllow === 'true' || rawAllow === '1' || rawAllow === 'yes';
    if (allowEmpty) {
      return { allowed: true, reason: 'empty-referrer-allowed' };
    }
    return {
      allowed: false,
      reason:
        'No referrer (embedding page URL not sent). Add REACT_APP_EMBED_ALLOW_EMPTY_REFERRER=true for local testing only, or use a referrer policy that sends the origin.',
    };
  }

  let parentOrigin;
  try {
    parentOrigin = new URL(ref).origin;
  } catch {
    return { allowed: false, reason: 'Invalid referrer URL' };
  }

  const explicit = parseEmbedOrigins();
  for (const rule of explicit) {
    if (originMatchesRule(parentOrigin, rule)) {
      return { allowed: true };
    }
  }

  // Default dev: allow localhost-style parents if no explicit list configured
  if (explicit.length === 0 && isLocalDevOrigin(parentOrigin)) {
    return { allowed: true };
  }

  if (explicit.length > 0) {
    return {
      allowed: false,
      reason: `Not allowed: unsupported origin`,
    };
  }

  return {
    allowed: false,
    reason:
      `Origin ${parentOrigin} is not allowed: REACT_APP_EMBED_ORIGINS was empty in this build. ` +
      'Set it at build time (e.g. docker build --build-arg REACT_APP_EMBED_ORIGINS="https://*.rejara.com,https://rejara.com") and redeploy. ' +
      'For local dev without a list, only localhost/127.0.0.1 parents are allowed.',
  };
}
